import * as React from 'react';
import { Check, Heart, Pencil, UsersRound } from 'lucide-react';
import { Card, CardContent, CardFooter, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { photoPackVariants, useBackgroundStyle } from './utils';
import { ModelImage } from '@acme/db';
import { PhotoPackGrid, PhotoPackName, PhotoPackTag } from '.';
import { Button } from '@components/ui/button';
import { Skeleton } from '@components/ui/skeleton';
import { useSession } from '@hooks/index';
interface SamplesProps {
  className?: string;
  enableSampleGallery?: boolean;
  checked?: boolean;
  style?: React.CSSProperties;
  tag?: string;
  onCustomize?: (checked: boolean) => void;
  relationship?: 'couple' | 'friends';
  useMeshGradient?: boolean;
  singleColor?: string;
  isSheetOpen?: boolean;
  setIsSheetOpen?: (open: boolean) => void;
  limitSamples?: number;
  showSelfies?: boolean;
  skeleton?: boolean;
  hideGetButton?: boolean;
  photoPack?: {
    name: string;
    id: string;
    tag: {
      name: string;
      url: string;
    };
    url: string;
    sampleImages?: (Pick<ModelImage, 'id' | 'imageUrl'> & { selfieImageUrl?: string | null })[];
    primaryDarkColor?: string;
    secondaryDarkColor?: string;
  };
  disableLink?: boolean;
}

export function Samples({
  className,
  photoPack,
  checked = false,
  style,
  onCustomize,
  relationship,
  useMeshGradient = false,
  singleColor,
  setIsSheetOpen,
  limitSamples = 2,
  showSelfies = false,
  disableLink = false,
  skeleton = false,
  hideGetButton = false
}: SamplesProps) {
  // Get background style
  const backgroundStyle = useBackgroundStyle({
    useMeshGradient,
    variant: 'samples',
    singleColor,
    primaryDarkColor: photoPack?.primaryDarkColor || null,
    secondaryDarkColor: photoPack?.secondaryDarkColor || null,
    style
  });

  const { user } = useSession();
  const isAdmin = user?.isAdmin;

  if (skeleton) {
    return (
      <Card className="w-full bg-foreground/10 animate-pulse">
        <CardContent className="p-4 pb-0">
          <div className="grid grid-cols-2 gap-1">
            {[...Array(2)].map((_, i) => (
              <Skeleton key={i} className="aspect-[2/3] w-full h-auto animate-none bg-foreground/10" />
            ))}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col items-start px-4 py-2">
          <Skeleton className="h-3 w-3/4 mb-2 animate-none bg-foreground/10" />
          <Skeleton className="h-3 w-1/3 mb-1 animate-none bg-foreground/10" />
        </CardFooter>
      </Card>
    );
  }

  // Create the card component once to avoid duplication
  const card = (
    <Card
      className={cn(photoPackVariants({ variant: 'samples', checked, className }), 'group')}
      style={backgroundStyle}
    >
      <CardContent className="p-0 pb-0">
        {isAdmin && photoPack?.id && (
          <Button
            variant="overlay"
            size="icon"
            className="absolute top-2 right-2 z-10 hidden group-hover:flex"
            onClick={() => window.open(`/admin/themes/${photoPack.id}`, '_blank')}
            title="Go to Theme Admin"
          >
            <Pencil className="h-3 w-3" />
            <span className="sr-only">Go to Theme Admin</span>
          </Button>
        )}

        <PhotoPackGrid
          limitSamples={limitSamples}
          displayImages={photoPack?.sampleImages?.slice(0, limitSamples) ?? []}
          disableLink={disableLink}
          setIsSheetOpen={setIsSheetOpen}
          photoPack={photoPack}
          forceTallAspectRatio={true}
          showSelfies={showSelfies}
          className="grid grid-cols-2 gap-0.5 bg-black"
        />

        {checked && (
          <div
            className="z-20 absolute inset-0 bg-background/80 flex items-center justify-center"
            onClick={() => {
              onCustomize?.(true);
            }}
          >
            <div className="w-10 h-10 bg-accent text-white ring-inset ring-2 ring-accent-foreground rounded-full shadow-sm flex items-center justify-center">
              <Check className="w-6 h-6" />
            </div>
          </div>
        )}
      </CardContent>

      <CardContent className="p-3 relative z-10">
        <div className="flex flex-row justify-between items-center w-full bg-red-500/0 h-auto gap-2">
          <div className="flex flex-col min-w-0 w-full">
            <CardTitle className="text-white text-base mb-1 self-stretch font-semibold leading-none">
              <PhotoPackName
                photoPack={photoPack!}
                disableLink={disableLink}
                className="text-white truncate break-all text-wrap line-clamp-1 w-full"
              />
            </CardTitle>
            <div className="flex flex-row justify-between items-center w-full mt-0 gap-2 text-wrap line-clamp-1 break-all">
              <PhotoPackTag
                photoPack={photoPack!}
                disableLink={disableLink}
                className="flex text-xs text-white/80 hover:underline capitalize"
              />
            </div>
            {relationship && (
              <div
                className={cn(
                  'flex flex-row items-center capitalize tracking-tight truncate w-full text-xs',
                  relationship === 'couple'
                    ? 'text-rose-300'
                    : relationship === 'friends'
                    ? 'text-green-300'
                    : 'text-blue-300'
                )}
              >
                {relationship === 'couple' && <Heart size={12} className="mr-1" />}
                {relationship === 'friends' && <UsersRound size={12} className="mr-1" />}
                {relationship}
              </div>
            )}
          </div>
          {!hideGetButton && (
            <Button
              size="xs"
              className="dark rounded-full text-blue-600 font-bold px-4"
              onClick={() => onCustomize?.(!checked)}
            >
              {checked ? 'Edit' : 'Get'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  // Conditionally wrap the card in a Link if disableLink is false
  return card;
}
